import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';

const ProgressiveImage = ({
  src,
  placeholderSrc,
  alt,
  sx,
  imgSx,
  loading,
  decoding,
  fetchPriority,
  onLoad,
  ...rest
}) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const handleLoad = (event) => {
    setIsLoaded(true);

    if (onLoad) {
      onLoad(event);
    }
  };

  return (
    <Box
      position={'relative'}
      overflow={'hidden'}
      sx={[
        {
          backgroundColor: 'grey.200',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {placeholderSrc ? (
        <Box
          aria-hidden={'true'}
          position={'absolute'}
          top={0}
          right={0}
          bottom={0}
          left={0}
          sx={{
            backgroundImage: `url(${placeholderSrc})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(18px)',
            transform: 'scale(1.06)',
            opacity: isLoaded ? 0 : 1,
            transition: 'opacity 0.35s ease-out',
          }}
        />
      ) : null}
      <Box
        component={'img'}
        src={src}
        alt={alt}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        onLoad={handleLoad}
        sx={[
          {
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: placeholderSrc ? (isLoaded ? 1 : 0) : 1,
            transform: placeholderSrc
              ? isLoaded
                ? 'scale(1)'
                : 'scale(1.02)'
              : 'none',
            transition: placeholderSrc
              ? 'opacity 0.45s ease-out, transform 0.6s ease-out'
              : 'none',
          },
          ...(Array.isArray(imgSx) ? imgSx : [imgSx]),
        ]}
      />
    </Box>
  );
};

ProgressiveImage.propTypes = {
  src: PropTypes.string.isRequired,
  placeholderSrc: PropTypes.string,
  alt: PropTypes.string,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  imgSx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  loading: PropTypes.oneOf(['eager', 'lazy']),
  decoding: PropTypes.oneOf(['async', 'auto', 'sync']),
  fetchPriority: PropTypes.oneOf(['auto', 'high', 'low']),
  onLoad: PropTypes.func,
};

ProgressiveImage.defaultProps = {
  placeholderSrc: '',
  alt: '',
  sx: undefined,
  imgSx: undefined,
  loading: 'lazy',
  decoding: 'async',
  fetchPriority: 'auto',
  onLoad: undefined,
};

export default ProgressiveImage;